/**
 * MIY Fresh storefront behaviour.
 *
 * Kept separate from theme.js so the Nest vendor file stays diffable against a
 * future theme update. Loaded deferred from layout/theme.liquid.
 *
 * Currently: product-card add-to-cart and quantity stepper behaviour.
 */
( function() {
	'use strict';

	/**
	 * Variant id -> quantity currently in the cart.
	 * Rebuilt from /cart.js rather than rendered into the markup, so it stays
	 * correct on a page served from cache and after any AJAX cart change.
	 */
	var cartQuantities = {};

	function fetchJSON( url, body ) {
		var options = body
			? {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify( body )
			}
			: { headers: { 'Accept': 'application/json' } };

		return fetch( url, options ).then( function( r ) {
			if ( ! r.ok ) {
				throw new Error( url + ' responded ' + r.status );
			}

			return r.json();
		} );
	}

	function routes() {
		var g = window.btyGlobals || {};

		return {
			add: g.cart_add_url || '/cart/add',
			change: g.cart_change_url || '/cart/change'
		};
	}

	// -- card helpers ------------------------------------------------------

	function selectedVariantId( card ) {
		var input = card.querySelector( '[data-miy-variant-input]' );

		return input ? input.value : null;
	}

	/**
	 * Show the stepper when this card's variant is in the cart, the ADD button
	 * when it is not. Re-run after every cart mutation so a card stays correct
	 * when the same product is changed from the cart page or another card.
	 */
	function renderCartState( card ) {
		var add = card.querySelector( '[data-miy-add]' );
		var stepper = card.querySelector( '[data-miy-stepper]' );
		var qtyEl = card.querySelector( '[data-miy-qty]' );
		if ( ! add || ! stepper ) {
			return;
		}

		var qty = cartQuantities[ selectedVariantId( card ) ] || 0;

		if ( qty > 0 ) {
			qtyEl.textContent = qty;
			stepper.hidden = false;
			add.hidden = true;
		} else {
			stepper.hidden = true;
			add.hidden = false;
		}
	}

	function renderAllCartStates() {
		document.querySelectorAll( '[data-miy-card]' ).forEach( renderCartState );
	}

	function setBusy( card, busy ) {
		card.classList.toggle( 'is-busy', !! busy );
	}

	/**
	 * Refresh the header cart count and mini-cart markup, reusing the theme's
	 * own helpers when they are present so both carts stay in agreement.
	 */
	function syncCartChrome( response ) {
		if ( typeof window.btyCartItemCount === 'function' && typeof response.item_count !== 'undefined' ) {
			window.btyCartItemCount( response.item_count );
		}

		var sections = response.sections;
		if ( ! sections || typeof window.btyGetSectionHtml !== 'function' ) {
			return;
		}

		var miniCart = document.querySelector( '.header-icons .mini-cart' );
		if ( miniCart && sections['mini-cart'] ) {
			miniCart.innerHTML = window.btyGetSectionHtml( sections['mini-cart'], '.mini-cart' );

			// Register any .lazy-image nodes the injected markup brought with it.
			if ( typeof window.btyAnimationImageLoad === 'function' ) {
				window.btyAnimationImageLoad( miniCart );
			}
		}
	}

	function refreshQuantities() {
		return fetchJSON( '/cart.js' ).then( function( cart ) {
			cartQuantities = {};
			( cart.items || [] ).forEach( function( item ) {
				cartQuantities[ item.variant_id ] = item.quantity;
			} );

			renderAllCartStates();

			return cart;
		} );
	}

	// -- actions -----------------------------------------------------------

	function addToCart( card ) {
		var id = selectedVariantId( card );
		if ( ! id ) {
			return;
		}

		setBusy( card, true );

		fetchJSON( routes().add, {
			items: [ { id: Number( id ), quantity: 1 } ],
			sections: [ 'mini-cart', 'cart-count' ]
		} )
			.then( function( res ) {
				syncCartChrome( res );

				// The add response does not carry line quantities, so re-read
				// the cart rather than guessing at the new total.
				return refreshQuantities();
			} )
			.catch( function( e ) {
				console.error( '[miy] add to cart failed', e );
			} )
			.finally( function() {
				setBusy( card, false );
			} );
	}

	function changeQuantity( card, delta ) {
		var id = selectedVariantId( card );
		var current = cartQuantities[ id ] || 0;
		var next = current + delta;
		if ( ! id || next < 0 ) {
			return;
		}

		setBusy( card, true );

		// /cart/change accepts a variant id for `id`. Quantity 0 removes the line.
		fetchJSON( routes().change, {
			id: String( id ),
			quantity: next,
			sections: [ 'mini-cart', 'cart-count' ],
			sections_url: window.location.pathname
		} )
			.then( function( res ) {
				syncCartChrome( res );

				cartQuantities = {};
				( res.items || [] ).forEach( function( item ) {
					cartQuantities[ item.variant_id ] = item.quantity;
				} );

				renderAllCartStates();
			} )
			.catch( function( e ) {
				console.error( '[miy] cart change failed', e );
			} )
			.finally( function() {
				setBusy( card, false );
			} );
	}

	/**
	 * Point the card's form at a different weight.
	 *
	 * The price is read from the option rather than refetched, so switching is
	 * instant, and the cart state is re-rendered because the newly selected
	 * weight may already be in the cart at a different quantity.
	 */
	function selectSize( card, select ) {
		var option = select.options[ select.selectedIndex ];
		if ( ! option ) {
			return;
		}

		var input = card.querySelector( '[data-miy-variant-input]' );
		if ( input ) {
			input.value = option.value;
		}

		var price = card.querySelector( '[data-miy-price]' );
		if ( price && option.dataset.price ) {
			price.innerHTML = option.dataset.price;
		}

		// Any compare-at price belonged to the previous weight; showing it
		// against a new price would be a false saving.
		var compare = card.querySelector( '[data-miy-compare]' );
		if ( compare ) {
			compare.remove();
		}

		renderCartState( card );
	}

	// -- wishlist ----------------------------------------------------------

	// The theme stores wishlist product ids as a JSON array under this key
	// (assets/theme.js:2415). Reading and writing the same key keeps the header
	// count and the wishlist page correct, without this card having to adopt the
	// vendor `.product-card` class and inherit all of its styling with it.
	var WISHLIST_KEY = 'wishlist-ids';

	function readWishlist() {
		try {
			var raw = localStorage.getItem( WISHLIST_KEY );
			var parsed = raw ? JSON.parse( raw ) : [];

			return Array.isArray( parsed ) ? parsed.map( String ) : [];
		} catch ( e ) {
			// Corrupt or unavailable storage (private mode) — treat as empty
			// rather than breaking every card on the page.
			return [];
		}
	}

	function writeWishlist( ids ) {
		try {
			localStorage.setItem( WISHLIST_KEY, JSON.stringify( ids ) );
		} catch ( e ) {
			return false;
		}

		if ( typeof window.btyUpdateCount === 'function' ) {
			window.btyUpdateCount( ids );
		}

		return true;
	}

	function renderWishlistState() {
		var ids = readWishlist();

		document.querySelectorAll( '[data-miy-wish]' ).forEach( function( btn ) {
			var on = ids.indexOf( String( btn.dataset.productId ) ) !== -1;
			btn.classList.toggle( 'is-active', on );
			btn.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
		} );
	}

	function toggleWishlist( btn ) {
		var id = String( btn.dataset.productId );
		var ids = readWishlist();
		var at = ids.indexOf( id );

		if ( at === -1 ) {
			ids.push( id );
		} else {
			ids.splice( at, 1 );
		}

		if ( writeWishlist( ids ) ) {
			renderWishlistState();
		}
	}

	// -- wiring ------------------------------------------------------------

	// One delegated listener on the document, so cards injected later by AJAX
	// (filtering, pagination, quickview) work with no re-binding.
	function onClick( e ) {
		var card = e.target.closest ? e.target.closest( '[data-miy-card]' ) : null;
		if ( ! card ) {
			return;
		}

		var wish = e.target.closest( '[data-miy-wish]' );
		if ( wish ) {
			e.preventDefault();
			toggleWishlist( wish );
			return;
		}

		var step = e.target.closest( '[data-miy-step]' );
		if ( step ) {
			e.preventDefault();
			changeQuantity( card, Number( step.dataset.miyStep ) );
			return;
		}

		var add = e.target.closest( '[data-miy-add]' );
		if ( add && ! add.disabled ) {
			// Cancels the form's native submit. The card deliberately does not
			// carry theme.js's .add-to-cart-button hook, so nothing else is
			// listening on this button.
			e.preventDefault();
			addToCart( card );
		}
	}

	function init() {
		if ( ! document.querySelector( '[data-miy-card]' ) ) {
			return;
		}

		document.addEventListener( 'click', onClick );

		// Delegated for the same reason as the click handler: cards injected
		// later by filtering or pagination work with no re-binding.
		document.addEventListener( 'change', function( e ) {
			var select = e.target.closest ? e.target.closest( '[data-miy-size]' ) : null;
			if ( ! select ) {
				return;
			}

			var card = select.closest( '[data-miy-card]' );
			if ( card ) {
				selectSize( card, select );
			}
		} );

		renderWishlistState();

		refreshQuantities().catch( function( e ) {
			// A failed read just means every card keeps showing ADD, which is
			// still usable — do not let it break the click wiring.
			console.error( '[miy] could not read cart', e );
		} );
	}

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}

	// Theme editor: re-sync when a section is re-rendered.
	document.addEventListener( 'shopify:section:load', function() {
		refreshQuantities().catch( function() {} );
	} );
}() );
